<template>
  <div class="containers-single">
    <SectionHeader>
      <template v-slot:breadcrumbs>
        <BreadCrumbs :items="[{ text: $t('routes.routes'), name: 'routes' }, { text: route.name }]" />
      </template>
    </SectionHeader>

    <LoadingSpinner v-if="loading" />
    <div v-else>
      <div class="body-content">
        <div class="content-wrap">
          <header class="content-header cf">
            <h3>{{ $t('meta.overview') }}</h3>
          </header>
          <div class="content-body">
            <div class="route-map-wrapper">
              <RoutesMap v-if="mapReady" :geojson="routeGeoJSON" :routeStops="route.stops" :isStatic="rendersStatic" />
              <LoadingSpinner v-else />
            </div>
            <div class="route-overview">
              <div v-if="route.duration">
                <h3>{{ $t('meta.duration') }}:</h3>
                <p>{{ parseFloat(route.duration).toFixed(2) }} h</p>
              </div>
              <div v-if="route.distance">
                <h3>{{ $t('meta.distance') }}:</h3>
                <p>{{ parseFloat(route.distance).toFixed(2) }} km</p>
              </div>
            </div>
          </div>
        </div>

        <div class="content-wrap">
          <header class="content-header cf">
            <h3>{{ $t('routes.stops') }}</h3>
          </header>
          <div class="content-body">
            <LoadingSpinner v-if="loadingStops" />
            <Table v-else class="table-route-show">
              <template #header>
                <th></th>
                <th></th>
                <th>{{ $t('routes.type') }}</th>
                <th>{{ $t('meta.location') }}</th>
                <th>{{ $t('routes.container') }}</th>
              </template>
              <template #body>
                <tr v-for="stop in routeStops" :key="stop.uuid">
                  <td>
                    <span v-if="getStopIcon(stop)">
                      <span :class="`stop-status status-${stop.status} material-icons`">{{ getStopIcon(stop) }}</span>
                    </span>
                    <span v-else>
                      <span class="material-icons">{{ stop.position_type == 'end' ? 'flag' : 'outlined_flag' }}</span>
                    </span>
                  </td>
                  <td>
                    <span>#{{ stop.position + 1 }}</span>
                  </td>
                  <td>
                    <span>{{ $t(`routes.${stop.position_type}`) }}</span>
                  </td>
                  <td>
                    <template v-if="stop.containers.length > 0">
                      <p class="container-group">
                        {{ stop.group_name }}
                      </p>
                    </template>
                    {{ stop.preformatted }}
                    <template v-for="aType in stopAttachmentTypes">
                      <StopAttachment
                        v-if="attachmentIsVisible(stop, aType)"
                        :key="aType"
                        :stop="stop"
                        :attachmentType="aType"
                      />
                    </template>
                  </td>
                  <td>
                    <ul v-if="stop.containers.length > 0">
                      <li v-for="container in stop.containers" :key="container.uuid">
                        <span :class="`stop-status status-${containerStatusClass(container.status)} material-icons`">{{
                          containerStatusIcon(container.status)
                        }}</span>
                        <span
                          ><Link :to="`/containers/${container.container.uuid}`">{{
                            container.container.type
                          }}</Link></span
                        >
                        <span v-if="container.status === 'emptied'">
                          <span v-if="container.fill_level >= 0.0" class="route-small">{{
                            $t('routes.fillLevelReport', {
                              fillLevel: parseInt(container.fill_level * 100),
                              date: formattedFromDateTime(container.measured_at),
                            })
                          }}</span>
                          <span v-if="container.fill_level < 0.0" class="route-small">{{
                            $t('routes.notEmptiedNotice', {
                              date: formattedFromDateTime(container.measured_at),
                            })
                          }}</span>
                        </span>
                      </li>
                    </ul>
                  </td>
                </tr>
              </template>
            </Table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BreadCrumbs from '@/components/BreadCrumbs'
import LoadingSpinner from '@/components/LoadingSpinner'
import SectionHeader from '@/components/SectionHeader'
import Table from '@/components/Table/index.vue'
import dateUtils from '@/components/mixins/dateUtils'
import RoutesMap from '@/components/routes/RoutesMap.vue'
import StopAttachment from '@/components/routes/StopAttachment.vue'

import routesApi from '@/api/routes'
import mapUtils from '@/utils/mapbox'
import routeStopUtils from '@/utils/routeStop'

export default {
  name: 'routeDetails',
  data: () => ({
    loading: true,
    loadingStops: true,
    map: null,
    route: {},
    routeStops: [],
    routeGeoJSON: {
      type: 'FeatureCollection',
      features: [],
    },
    mapReady: false,
    stopAttachmentTypes: ['comment', 'image', 'dumped_weights'],
  }),
  mixins: [dateUtils, mapUtils, routeStopUtils],
  components: { BreadCrumbs, LoadingSpinner, RoutesMap, SectionHeader, StopAttachment, Table },
  computed: {
    rendersStatic() {
      return !this.route.geojson || Object.keys(this.route.geojson).length === 0 || this.$route.query.geojson !== 'wdl'
    },
  },
  methods: {
    attachmentIsVisible(stop, currentType) {
      return (
        this.stopAttachmentTypes.some(
          aType => currentType === aType && stop[aType] && Object.keys(stop[aType]).length,
        ) &&
        (stop.position_type !== 'dumping' || currentType !== 'image')
      )
    },
    async getRoutesGeojson(route) {
      const result = await routesApi.getGeoJson(route.uuid)
      if (result) {
        this.loadMap(result)
      }
    },
    loadMap(mapData) {
      this.routeGeoJSON = mapData
      this.loading = false
      setTimeout(() => {
        this.mapReady = true
      }, 1000)
    },
    containerStatusClass(status) {
      return status == null ? 'pending' : status
    },
    containerStatusIcon(status) {
      return this.getStopIcon({ position_type: 'container', status: status })
    },
  },
  async mounted() {
    this.route = await routesApi.get(this.$route.params.uuid)
    if (this.rendersStatic) {
      this.getRoutesGeojson(this.route)
    } else {
      this.loadMap(this.route.geojson)
    }

    this.routeStops = await routesApi.getStops(this.$route.params.uuid)
    this.loadingStops = false
  },
}
</script>

<style lang="scss">
.stop-status {
  width: 24px;
  height: 24px;
  background: #ffba00;
  color: #fff;
  padding: 3px;
  font-size: 18px;
  border-radius: 50%;
}

.status-pending {
  background: #ffba00;
}

.status-emptied {
  background: #1c9600;
}

.status-skipped {
  background: #31b7bc;
}

.table-route-show tbody tr td .container-group {
  font-weight: 600;
  margin-top: 0;
  margin-bottom: 0.5rem;
}

.table-route-show tbody tr td ul {
  margin: 0;
  padding: 0;

  li {
    list-style-type: none;
    margin-bottom: 12px;
    background: #f9f9f9;
    padding: 8px;
    border-radius: var(--border-radius-m);
    border: 1px solid #f1f1f1;

    span {
      display: inline-block;
      vertical-align: middle;
      margin-right: 10px;
    }

    .route-small {
      display: block;
      font-size: 14px;
      padding-left: 33px;
    }
  }

  li:last-child {
    margin-bottom: 0;
  }
}

.route-map-wrapper {
  width: calc(100% - 350px);
  height: 400px;
}

#map-route {
  height: 400px;
  margin-bottom: 0;
}

.route-overview {
  position: absolute;
  width: 350px;
  right: 0;
  top: 0;
  padding: 20px;

  div {
    margin-bottom: 20px;
    background: #f9f9f9;
    border-radius: 4px;
    border: 1px solid #f1f1f1;
    padding: 15px;
  }

  h3 {
    margin: 0 0 5px 0;
    font-weight: 400;
    font-size: 16px;
    text-transform: uppercase;
  }

  p {
    margin: 0;
    font-size: 28px;
    font-weight: 600;
  }
}
</style>
